import React, { useEffect, useRef, useState } from 'react';
import { StoryblokComponent } from './types';
import { TeaserService } from '../../services/teaser';
import { PageItem } from '../../services/teaser/types';
import { StringService } from '../../services/string';
import { StoryblokService } from '../../services/storyblok';
import { blokToComponent } from '../../helpers/blok-to-component';
import { ConditionalWrapper } from '../../helpers/conditional-wrapper';
import { filterProps } from '../../helpers/filter-props';
import { parseAssetSource } from '../../utils/asset';
import { BaseBlok, BlokToComponentProps } from '../../helpers/blok-to-component/types';
import { RocheTeaser as RocheTeaserType } from '../../types/global';

type RocheTeaserManualProps = BlokToComponentProps<
  RocheTeaserType &
    BaseBlok & {
      page: { story: { id: number; preRenderContent?: any } };
    }
>;

const warningDescription = `
  If you wish to proceed with this action, please publish the linked page first.
  Note that if the linked page on the teaser remains unpublished,
  this teaser will not show up on the live version of the page.
`;

const renderWarning = (keyForChildren: string | number, slot: string, element: React.MutableRefObject<unknown>) => (
  <roche-teaser
    ref={element}
    key={keyForChildren}
    layout='teaser-variant-b-without-image'
    teaser-title='WARNING: This teaser is not published!'
    teaser-description={warningDescription}
    loaded={true}
    slot={slot}
    unpublished={true}
  ></roche-teaser>
);
export const RocheTeaser = ({
  blok,
  updateHoistedState,
  wrapperTag,
  keyForChildren,
  slot,
  getComponent,
}: RocheTeaserManualProps): JSX.Element => {
  const element = useRef(null);
  const [teaser, setTeaser] = useState<PageItem>(blok?.page?.story?.preRenderContent || ({} as PageItem));
  const Wrapper = wrapperTag as React.ElementType;

  useEffect(() => {
    const storyId = blok.page?.story?.id;
    const hasTeaser = teaser && Object.keys(teaser).length > 0;
    const { _uid: blokUid } = blok;

    if (!storyId && !hasTeaser) {
      updateHoistedState?.(0, 0, '', {});
      return;
    }
    if (!hasTeaser) {
      // no existing teaser found, so we fetch the teaser
      TeaserService.getTeaser(blok.page.story.id).then((result) => {
        if (!element.current) {
          return;
        }
        if (result) {
          setTeaser(result);
          updateHoistedState?.(1, 1, blokUid, result);
        }
      });
      return;
    }
    // we need to delay the updateHoistedState due to livecycle issues in parent components (walls, teaser-auto)
    setTimeout(() => {
      updateHoistedState?.(1, 1, blokUid, teaser);
    }, 0);
  }, []);

  const { slotted } = filterProps(blok);
  const slottedComponents = Object.keys(slotted).flatMap((key) =>
    (slotted[key] as StoryblokComponent[]).map((component) =>
      component.component === 'roche-button'
        ? {
            ...component,
            slot: key,
            'no-margin': blok.layout.indexOf('teaser--card') === 0,
            appearance: 'tertiary',
          }
        : {
            ...component,
            slot: key,
          },
    ),
  );
  const teaserImage = teaser.content?.teaser_image?.[0];
  const isTeaserInDraft = Object.keys(teaser).length <= 0;

  return (
    <ConditionalWrapper
      key={keyForChildren}
      condition={wrapperTag !== undefined && wrapperTag !== ''}
      wrapper={(children: JSX.Element): JSX.Element => <Wrapper slot={slot}>{children}</Wrapper>}
    >
      {isTeaserInDraft && StoryblokService.isInEditor() ? (
        renderWarning(keyForChildren, slot, element)
      ) : (
        <roche-teaser
          ref={element}
          key={keyForChildren}
          layout={blok.layout}
          teaser-image={teaserImage?.brightcove_id || parseAssetSource(teaserImage?.src.filename)}
          teaser-title={teaser.content?.teaser_title}
          teaser-overline={teaser.content?.teaser_overline}
          teaser-description={teaser.content?.teaser_description}
          teaser-date={teaser?.first_published_at}
          teaser-url={StringService.sanitizeUrl(`/${teaser?.full_slug}`)}
          loaded={teaser.content !== undefined}
          slot={slot}
          is-teaser-in-draft={isTeaserInDraft}
        >
          {slottedComponents.map((component, index) =>
            blokToComponent({ blok: { ...component }, getComponent }, `${index}`),
          )}
        </roche-teaser>
      )}
    </ConditionalWrapper>
  );
};
